import { createContext, useEffect, useState, ReactElement } from 'react';

import { ServerError, Unauthorized } from '@ohpen/enso-ui';
import { useConfig } from '@ohpen/react-utils';
import { useAuth } from '@ohpen/react-auth';
import { ConfigurationResponse, configurationsGetConfiguration, client } from '@pmp/api';
import { IPmpConfigProviderProps, PmpConfig } from './interfaces';

const isLocal = globalThis.location.hostname === 'localhost';

// Local overrides — values here win over the API response when running on localhost.
// NOTE: localFeatureToggles takes precedence (spread order: { ...data.featureToggles, ...localFeatureToggles }).
// Playwright tests can still mock the API to control flags not listed here.
const localFeatureToggles = {
  enableImports: true,
  enableFeesImport: true,
  enableFeeProfilesImport: true,
  enableExports: true,
  enableSaveView: true,
  enableProductGroups: false,
  enableProductsCrud: true,
  enableProductsBulkCrud: true,
  enableErcProfilesCrud: true,
  enableFeeProfilesCrud: true,
  enableFeesCrud: true,
  enableAdditionalFields: true,
  enableEditingLiveProducts: false,
  enableExclusivesCrud: true,
  enableExchangeRates: true,
  enableLender: true,
  enableLenderCrud: true,
  enableLenderProcurationFeesCrud: true,
  enableFirms: true,
  enableFirmsCrud: true,
  enableBrokers: true,
  enableBrokersCrud: true,
  enableProcurationFees: true,
  enableProcurationFeesCrud: false,
  enableProcurationFeeRulesCrud: false,
  enableFourEyes: true,
  enableDarkMode: true,
};

export const PmpConfigContext = createContext<PmpConfig>({} as PmpConfig);

export const PmpConfigProvider = ({ children, getAccessToken }: IPmpConfigProviderProps): ReactElement => {
  const config = useConfig();
  const auth = useAuth();
  const brokerPortalUrl = (config as any).APP?.brokerPortalUrl || '';
  const [pmpConfig, setPmpConfig] = useState<PmpConfig | null>(null);
  const [error, setError] = useState(false);
  const [error401, setError401] = useState(false);
  const [isFetching, setIsFetching] = useState(false);

  useEffect(() => {
    // Don't fetch until we have an authenticated user
    if (!auth.isAuthenticated || pmpConfig || isFetching) return;

    // Try to load from cache first for faster initial render
    try {
      const cachedPmpConfig = localStorage.getItem('pmpConfig');
      if (cachedPmpConfig) {
        setPmpConfig(JSON.parse(cachedPmpConfig));
      }
    } catch (e) {
      console.error('Error parsing cached PMP config:', e);
      localStorage.removeItem('pmpConfig');
    }

    // Always fetch fresh config from API
    setIsFetching(true);

    getAccessToken()
      .then(token => {
        if (!token) {
          setError(true);
          setIsFetching(false);
          throw new Error('No access token');
        }
        // Use client directly with headers
        return client.get({
          url: '/configuration/configuration',
          headers: {
            Authorization: `Bearer ${token}`,
          },
          parseAs: 'json',
        }) as Promise<{ data: ConfigurationResponse }>;
      })
      .then(result => {
        if (!result) return;

        const { data } = result as { data: ConfigurationResponse };
        const config = isLocal
          ? { ...data, brokerPortalUrl, featureToggles: { ...data.featureToggles, ...localFeatureToggles } }
          : { ...data, brokerPortalUrl };

        setPmpConfig(config);
        localStorage.setItem('pmpConfig', JSON.stringify(config));
        setIsFetching(false);
      })
      .catch((err: unknown) => {
        // Check if it's a 401 Unauthorized error (try different error structures)
        const is401 =
          err &&
          typeof err === 'object' &&
          (('status' in err && err.status === 401) ||
            ('response' in err &&
              err.response &&
              typeof err.response === 'object' &&
              'status' in err.response &&
              err.response.status === 401));

        if (is401) {
          setError401(true);
        } else {
          setError(true);
        }
        setIsFetching(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [brokerPortalUrl, pmpConfig, auth.isAuthenticated, getAccessToken]);

  if (error401) return <Unauthorized />;
  if (error) return <ServerError />;

  return <PmpConfigContext.Provider value={pmpConfig ?? ({} as PmpConfig)}>{children}</PmpConfigContext.Provider>;
};
